import type { ProspectResponse } from '@common/types/prospects';
import { type SxProps, type Theme, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';
import { useState } from 'react';
import FullScreenLoader from '@/components/FullScreenLoader';
import {
    BORROWER_INFORMATION,
    CONTACT_DETAILS,
    CURRENT_ADDRESS,
    INITIAL_DETAILS,
    LENDER_INFORMATION,
    MAILING_INFO,
    PROPERTY_DETAILS,
    PURCHASER_INFORMATION,
    SELLER_INFORMATION,
    uidRegex,
} from '@/constants';
import { getFileDetails } from '@/services/FileService';
import { downloadV1IntakeSummaryPdf, getIntakeFormResponse } from '@/services/ProspectService';
import { getIntakeTemplateForms } from '@/services/TemplateFormsService';
import { isAxiosNotFoundError } from '@/utils/queryError';
import { errorSnackbar } from '@/utils/snackbar';
import ProspectDetailsEmptyPage from '../ProspectDetailsEmptyPage';
import ProspectDetailsErrorPage from '../ProspectDetailsErrorPage';
import {
    applyIntakeFieldDisplayFormatting,
    buildTemplateFieldsById,
} from './intakeFieldFormatting';
import RealEstateIntakeInformation from './RealEstateIntakeInformation';
import type { IntakeResponseObj } from './types';

const TAB_ORDER = [
    INITIAL_DETAILS,
    CONTACT_DETAILS,
    MAILING_INFO || CURRENT_ADDRESS,
    PROPERTY_DETAILS,
];

function triggerDownload(url: string, filename: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const handleDocumentDownload = async (fileUrl, fileName) => {
    if (!fileUrl || !fileName) {
        errorSnackbar('File does not exist');
        return;
    }
    try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();

        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;

        a.click();

        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading the file:', error);
    }
};

const isUUID = (value) => {
    //Split the value by '|' first and then by ','
    return value
        ?.split('|')
        ?.filter(Boolean) //removes empty strings from the array
        ?.every((part) =>
            part
                ?.split(',')
                ?.filter(Boolean)
                ?.every((subPart) => uidRegex.test(subPart.trim())),
        );
};

const getGroupedData = (data: IntakeResponseObj[], sortOrder: string[] = []) => {
    let groupedData = {};
    data.forEach((d) => {
        if (d.FormGroupTitle !== null) {
            if (!(d.FormGroupTitle in groupedData)) {
                groupedData[d.FormGroupTitle] = [];
            }

            // Add condition to prepend "$" if required
            const fieldsToPrependDollar = [
                'purchasePrice',
                'salePrice',
                'totalLoanAmount',
                'depositGivenPrice',
                'approxAmountOwing',
            ];
            if (
                d.FormGroupTitle === PROPERTY_DETAILS &&
                fieldsToPrependDollar.includes(d.Name) &&
                d.Value !== null &&
                d.Value !== ''
            ) {
                d.Value = `$${d.Value}`;
            }
            groupedData[d.FormGroupTitle].push(d);
        } else {
            try {
                if (!(d.Name in groupedData)) {
                    groupedData[d.Name] = [];
                }
                JSON.parse(d.Value).forEach((dIn) => {
                    groupedData[d.Name].push(JSON.parse(dIn));
                });
            } catch (err) {
                console.error(err);
            }
        }
    });
    if (sortOrder.length) {
        const temp = cloneDeep(groupedData);
        groupedData = {};
        sortOrder.forEach((s) => {
            if (s in temp) {
                groupedData[s] = temp[s];
            }
        });
        // Include any unsorted fields that were not in sortOrder
        Object.keys(temp).forEach((key) => {
            if (!(key in groupedData)) {
                groupedData[key] = temp[key];
            }
        });
    }
    // Add firstId and secondId to the CONTACT_DETAILS key
    const keysToCheck = [
        PURCHASER_INFORMATION,
        BORROWER_INFORMATION,
        SELLER_INFORMATION,
        LENDER_INFORMATION,
    ];
    keysToCheck.forEach((key) => {
        if (groupedData[key] && groupedData[CONTACT_DETAILS]) {
            groupedData[key].forEach((field) => {
                if (isUUID(field.Value)) {
                    groupedData[CONTACT_DETAILS].forEach((f) => {
                        f.push(field);
                    });
                }
            });
        }
    });

    return groupedData;
};

const handleUploadFields = async (data: {
    [key: string]: IntakeResponseObj[] | IntakeResponseObj[][];
}) => {
    const groupedDataWithFields: any = {};

    for (const tab of Object.keys(data)) {
        if (data[tab]) {
            for (const [fieldPosition, field] of data[tab].entries()) {
                if (Array.isArray(field)) {
                    // Handle array of wrapped questions
                    const tempWrappedQuestions: IntakeResponseObj[] = [];

                    for (const fieldIndex in field) {
                        const currentField = field[fieldIndex];

                        if (isUUID(currentField.Value)) {
                            const idGroups = currentField.Value.split('|');
                            if (idGroups.length === data[tab].length) {
                                const fieldIds: string[] =
                                    idGroups[fieldPosition]?.split(',') ?? [];
                                const apiCalls = fieldIds
                                    .filter(Boolean)
                                    .map((id) => getFileDetails(id));

                                const apiResp = await Promise.allSettled(apiCalls);
                                apiResp.forEach((promise) => {
                                    if (promise.status === 'fulfilled') {
                                        const file = promise.value.data;
                                        tempWrappedQuestions.push({
                                            ...currentField,
                                            Value: file.fileName,
                                            _fileId: file.fileId,
                                        });
                                    }
                                });
                            } else {
                                tempWrappedQuestions.push({ ...currentField });
                            }
                        } else if (uidRegex.test(currentField.Value?.split(',')[0])) {
                            const fieldIds: string[] = currentField.Value?.split(',');
                            const apiCalls = fieldIds
                                .filter(Boolean)
                                .map((id) => getFileDetails(id));

                            const apiResp = await Promise.allSettled(apiCalls);
                            apiResp.forEach((promise) => {
                                if (promise.status === 'fulfilled') {
                                    const file = promise.value.data;
                                    tempWrappedQuestions.push({
                                        ...currentField,
                                        Value: file.fileName,
                                        _fileId: file.fileId,
                                    });
                                }
                            });
                        } else {
                            tempWrappedQuestions.push({ ...currentField });
                        }
                    }

                    if (!groupedDataWithFields[tab]) {
                        groupedDataWithFields[tab] = [];
                    }

                    groupedDataWithFields[tab].push(tempWrappedQuestions);
                } else if (isUUID(field.Value) && field.Value.includes('|')) {
                    // Handle firstId and secondId fields specially
                    const idGroups = field.Value.split('|');

                    for (const idGroup of idGroups) {
                        const fieldIds: string[] = idGroup.split(',');
                        const apiCalls = fieldIds.filter(Boolean).map((id) => getFileDetails(id));

                        const apiResp = await Promise.allSettled(apiCalls);
                        apiResp.forEach((promise) => {
                            if (promise.status === 'fulfilled') {
                                const file = promise.value.data;
                                if (!groupedDataWithFields[tab]) {
                                    groupedDataWithFields[tab] = [];
                                }

                                groupedDataWithFields[tab].push({
                                    ...field,
                                    Value: file.fileName,
                                    _fileId: file.fileId,
                                });
                            }
                        });
                    }
                } else if (uidRegex.test(field.Value?.split(',')[0])) {
                    // For normal tabs questions
                    const fieldIds: string[] = field.Value?.split(',');
                    const apiCalls = fieldIds.filter(Boolean).map((id) => getFileDetails(id));

                    const apiResp = await Promise.allSettled(apiCalls);
                    apiResp.forEach((promise) => {
                        if (promise.status === 'fulfilled') {
                            const file = promise.value.data;
                            if (!groupedDataWithFields[tab]) {
                                groupedDataWithFields[tab] = [];
                            }

                            groupedDataWithFields[tab].push({
                                ...field,
                                Value: file.fileName,
                                _fileId: file.fileId,
                            });
                        }
                    });
                } else {
                    // Add rest of the fields
                    if (!groupedDataWithFields[tab]) {
                        groupedDataWithFields[tab] = [];
                    }

                    groupedDataWithFields[tab].push({ ...field });
                }
            }
        }
    }
    return groupedDataWithFields;
};

interface Props {
    prospectId?: string;
    prospect: ProspectResponse;
    selectedVersionId: number | null;
}

export default function IntakeResponseInformation({
    prospectId,
    prospect: prospectDetails,
    selectedVersionId,
}: Props) {
    const theme = useTheme();
    const [isIntakePdfDownloading, setIsIntakePdfDownloading] = useState<boolean>(false);
    const quoteTemplateId = prospectDetails?.quoteTemplate?.quoteTemplateId;

    const {
        data: intakeQueryData = { intakeResp: {}, isDraft: false },
        isPending,
        isError,
        isFetching,
        error,
        refetch,
    } = useQuery({
        queryKey: ['intake-response-information', prospectId, selectedVersionId, quoteTemplateId],
        queryFn: async () => {
            if (!prospectId) {
                return { intakeResp: {}, isDraft: false };
            }

            const [intakeFormDataResponse, templateFormResponse] = await Promise.all([
                getIntakeFormResponse(prospectId, selectedVersionId),
                quoteTemplateId
                    ? getIntakeTemplateForms(quoteTemplateId, true)
                    : Promise.resolve(null),
            ]);
            const groupedData = getGroupedData(
                JSON.parse(intakeFormDataResponse.data.formResponse),
                TAB_ORDER,
            );
            const groupedDataWithUploadFields = await handleUploadFields(groupedData);
            const templateFieldsById = buildTemplateFieldsById(
                templateFormResponse?.data.templateFormFields ?? [],
            );

            return {
                intakeResp: applyIntakeFieldDisplayFormatting(
                    groupedDataWithUploadFields,
                    templateFieldsById,
                ),
                isDraft: intakeFormDataResponse.data.isDraft ?? false,
                draftLastUpdatedAt: intakeFormDataResponse.data.draftLastUpdatedAt,
            };
        },
        enabled: !!prospectId,
        staleTime: 5 * 60_000,
    });

    const { intakeResp, isDraft, draftLastUpdatedAt } = intakeQueryData;

    const handleIntakePdfDownload = async (prospectId) => {
        const pdfFileName = `intake_${prospectDetails.contact.lastName}.pdf`;
        const response = await downloadV1IntakeSummaryPdf(prospectId, selectedVersionId);
        const url = URL.createObjectURL(response.data);
        triggerDownload(url, pdfFileName);
        URL.revokeObjectURL(url);
    };

    const matterName = prospectDetails.matterType;
    const responseDetailsContainerStyles: SxProps<Theme> = {
        padding: '24px',
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        alignItems: 'flex-start',
        backgroundColor: theme.palette.neutral.white,
    };

    const accordionStyles: SxProps<Theme> = {
        width: '100%',
        border: `1px solid ${theme.palette.neutral.N_300}`,
        borderRadius: '4px !important',
        boxShadow: 'none !important',
        '&.MuiAccordion-root.Mui-expanded': {
            boxShadow: '0px 4px 10px 0px #2F303233 !important',
            border: '0px !important',
        },

        '&::before': {
            content: 'none',
        },
    };

    const accordionDetailsStyles: SxProps<Theme> = {
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        gap: '16px',
    };

    const matterIntakeProps = {
        responseDetailsContainerStyles,
        isIntakePdfDownloading,
        intakeResponseData: intakeResp,
        accordionStyles,
        prospectId,
        accordionDetailsStyles,
        handleDocumentDownload,
        setIsIntakePdfDownloading,
        handleIntakePdfDownload,
    };

    if (isPending) {
        return <FullScreenLoader />;
    }

    if (isError && isAxiosNotFoundError(error)) {
        return <ProspectDetailsEmptyPage message="Intake response not received from client yet" />;
    }

    if (isError) {
        return (
            <ProspectDetailsErrorPage
                message="There was an error fetching the intake form response"
                onRetry={refetch}
                isRetrying={isFetching}
            />
        );
    }

    if (
        matterName === 'Sale' ||
        matterName === 'Purchase' ||
        matterName === 'Refinance' ||
        matterName === 'PrivateLender'
    ) {
        return (
            <RealEstateIntakeInformation
                matterType={matterName}
                {...matterIntakeProps}
                isDraft={isDraft}
                draftLastUpdatedAt={draftLastUpdatedAt}
            />
        );
    }
}
